import {
    bbox as turfBbox,
    flattenEach,
    multiLineString,
    point,
    pointToLineDistance,
} from '@turf/turf';

const getLeaflet = () => (typeof window !== 'undefined' ? window.L : null);
const METERS_PER_DEGREE_LATITUDE = 111320;
const MIN_LONGITUDE_COSINE = 0.01;
const WEB_MERCATOR_METERS_PER_PIXEL = 156543.03392804097;
const MAX_WEB_MERCATOR_LATITUDE = 85.05112878;
const MIN_TOUCH_RADIUS_METERS = 0.05;
const MAX_TOUCH_RADIUS_METERS = 3;

export function parseGeoJson(value) {
    if (!value) {
        return null;
    }

    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed.length) {
            return null;
        }

        try {
            return parseGeoJson(JSON.parse(trimmed));
        } catch {
            return null;
        }
    }

    if (typeof value !== 'object') {
        return null;
    }

    if (value.type === 'Feature' || value.type === 'FeatureCollection') {
        return value;
    }

    if (value.type && (value.coordinates || value.geometries)) {
        return value;
    }

    if (value.polygon != null) {
        return parseGeoJson(value.polygon);
    }

    if (value.geojson != null) {
        return parseGeoJson(value.geojson);
    }

    if (value.geometry) {
        const geometry = parseGeoJson(value.geometry);
        if (!geometry) {
            return null;
        }

        if (geometry.type === 'Feature' || geometry.type === 'FeatureCollection') {
            return geometry;
        }

        return {
            type: 'Feature',
            geometry,
            properties: value.properties ?? {},
        };
    }

    return null;
}

export function normalizeGeoJson(value) {
    const geojson = parseGeoJson(value);
    if (!geojson) {
        return null;
    }

    const geometry = geojson.type === 'Feature' ? geojson.geometry : geojson;
    if (geometry?.type === 'MultiLineString') {
        return multiLineString(geometry.coordinates);
    }

    return geojson;
}

function containsLineGeometry(geojson) {
    if (!geojson) {
        return false;
    }

    if (geojson.type === 'LineString' || geojson.type === 'MultiLineString') {
        return true;
    }

    if (geojson.type === 'Feature') {
        return containsLineGeometry(geojson.geometry);
    }

    if (geojson.type === 'FeatureCollection') {
        return (geojson.features ?? []).some(containsLineGeometry);
    }

    if (geojson.type === 'GeometryCollection') {
        return (geojson.geometries ?? []).some(containsLineGeometry);
    }

    return false;
}

export function hasLineGeometry(value) {
    return containsLineGeometry(parseGeoJson(value));
}

export function distanceToLineMeters(value, coordinate) {
    const geojson = parseGeoJson(value);
    const longitude = Number(Array.isArray(coordinate) ? coordinate[0] : coordinate?.lng ?? coordinate?.longitude);
    const latitude = Number(Array.isArray(coordinate) ? coordinate[1] : coordinate?.lat ?? coordinate?.latitude);
    if (!geojson || !Number.isFinite(longitude) || !Number.isFinite(latitude)) {
        return null;
    }

    const tapPoint = point([longitude, latitude]);
    let nearestDistance = null;

    try {
        flattenEach(geojson, feature => {
            if (feature?.geometry?.type !== 'LineString') {
                return;
            }

            try {
                const distance = pointToLineDistance(tapPoint, feature, { units: 'meters' });
                if (Number.isFinite(distance) && (nearestDistance === null || distance < nearestDistance)) {
                    nearestDistance = distance;
                }
            } catch {
                // Ignore malformed line parts while preserving valid parts from the same geometry.
            }
        });
    } catch {
        return null;
    }

    return nearestDistance;
}

export function getTouchRadiusMeters(latitude, zoom, targetSizePixels = 44) {
    const parsedLatitude = Number(latitude);
    const parsedZoom = Number(zoom);
    const parsedTargetSize = Number(targetSizePixels);
    if (!Number.isFinite(parsedLatitude)
        || Math.abs(parsedLatitude) > 90
        || !Number.isFinite(parsedZoom)
        || parsedZoom < 0
        || !Number.isFinite(parsedTargetSize)
        || parsedTargetSize <= 0) {
        return null;
    }

    const mercatorLatitude = Math.min(Math.abs(parsedLatitude), MAX_WEB_MERCATOR_LATITUDE);
    const metersPerPixel = WEB_MERCATOR_METERS_PER_PIXEL
        * Math.cos(mercatorLatitude * Math.PI / 180)
        / (2 ** parsedZoom);
    const radius = metersPerPixel * parsedTargetSize / 2;

    return Math.min(MAX_TOUCH_RADIUS_METERS, Math.max(MIN_TOUCH_RADIUS_METERS, radius));
}

function normalizePosition(position) {
    if (!Array.isArray(position) || position.length < 2) {
        return null;
    }

    const normalized = position
        .map((value) => Number(value))
        .filter((value, index) => index < 2 || Number.isFinite(value));

    if (!Number.isFinite(normalized[0]) || !Number.isFinite(normalized[1])) {
        return null;
    }

    return normalized;
}

function positionsEqual(first, second) {
    return Array.isArray(first)
        && Array.isArray(second)
        && first[0] === second[0]
        && first[1] === second[1];
}

function positionKey(position) {
    return `${position[0]},${position[1]}`;
}

function normalizeLinearRing(ring) {
    if (!Array.isArray(ring)) {
        return null;
    }

    const normalized = [];

    ring.forEach((position) => {
        const candidate = normalizePosition(position);
        if (!candidate) {
            return;
        }

        if (!normalized.length || !positionsEqual(normalized[normalized.length - 1], candidate)) {
            normalized.push(candidate);
        }
    });

    if (!normalized.length) {
        return null;
    }

    if (!positionsEqual(normalized[0], normalized[normalized.length - 1])) {
        normalized.push([...normalized[0]]);
    }

    if (normalized.length < 4) {
        return null;
    }

    const uniqueVertexCount = new Set(normalized.slice(0, -1).map(positionKey)).size;
    if (uniqueVertexCount < 3) {
        return null;
    }

    return normalized;
}

function sanitizePolygonCoordinates(coordinates) {
    if (!Array.isArray(coordinates) || !coordinates.length) {
        return null;
    }

    const shell = normalizeLinearRing(coordinates[0]);
    if (!shell) {
        return null;
    }

    const holes = coordinates
        .slice(1)
        .map(normalizeLinearRing)
        .filter(Boolean);

    return [shell, ...holes];
}

export function sanitizeGeoJsonForMapSimplification(value) {
    const geojson = parseGeoJson(value);
    if (!geojson) {
        return null;
    }

    if (geojson.type === 'Feature') {
        const geometry = sanitizeGeoJsonForMapSimplification(geojson.geometry);
        if (!geometry) {
            return null;
        }

        return {
            ...geojson,
            geometry,
        };
    }

    if (geojson.type === 'FeatureCollection') {
        const features = (geojson.features ?? [])
            .map((feature) => sanitizeGeoJsonForMapSimplification(feature))
            .filter(Boolean);

        return features.length
            ? { ...geojson, features }
            : null;
    }

    if (geojson.type === 'GeometryCollection') {
        const geometries = (geojson.geometries ?? [])
            .map((geometry) => sanitizeGeoJsonForMapSimplification(geometry))
            .filter(Boolean);

        return geometries.length
            ? { ...geojson, geometries }
            : null;
    }

    if (geojson.type === 'Polygon') {
        const coordinates = sanitizePolygonCoordinates(geojson.coordinates);

        return coordinates
            ? { ...geojson, coordinates }
            : null;
    }

    if (geojson.type === 'MultiPolygon') {
        const coordinates = (geojson.coordinates ?? [])
            .map(sanitizePolygonCoordinates)
            .filter(Boolean);

        return coordinates.length
            ? { ...geojson, coordinates }
            : null;
    }

    return geojson;
}

export function getGeoJsonBounds(value) {
    const geojson = parseGeoJson(value);
    if (!geojson) {
        return null;
    }

    try {
        const bounds = turfBbox(geojson);
        if (!Array.isArray(bounds) || bounds.length !== 4 || bounds.some((item) => !Number.isFinite(item))) {
            return null;
        }

        return bounds;
    } catch {
        return null;
    }
}

export function extendLeafletBounds(leafletBounds, geoJsonBounds, leaflet = getLeaflet()) {
    if (!leafletBounds || !Array.isArray(geoJsonBounds) || geoJsonBounds.length !== 4) {
        return false;
    }

    const [minLng, minLat, maxLng, maxLat] = geoJsonBounds;
    const values = [minLng, minLat, maxLng, maxLat];
    if (values.some((item) => !Number.isFinite(item))) {
        return false;
    }

    const southWest = leaflet?.latLng ? leaflet.latLng(minLat, minLng) : [minLat, minLng];
    const northEast = leaflet?.latLng ? leaflet.latLng(maxLat, maxLng) : [maxLat, maxLng];

    leafletBounds.extend(southWest);
    leafletBounds.extend(northEast);

    return true;
}

export function padGeoJsonBoundsByMeters(geoJsonBounds, paddingMeters = 0) {
    if (!Array.isArray(geoJsonBounds) || geoJsonBounds.length !== 4) {
        return null;
    }

    const [minLng, minLat, maxLng, maxLat] = geoJsonBounds.map(Number);
    const values = [minLng, minLat, maxLng, maxLat];
    if (values.some((item) => !Number.isFinite(item))) {
        return null;
    }

    const meters = Number(paddingMeters);
    if (!Number.isFinite(meters) || meters <= 0) {
        return [minLng, minLat, maxLng, maxLat];
    }

    const centerLat = (minLat + maxLat) / 2;
    const latitudePadding = meters / METERS_PER_DEGREE_LATITUDE;
    const longitudeScale = Math.max(
        Math.abs(Math.cos(centerLat * Math.PI / 180)),
        MIN_LONGITUDE_COSINE,
    );
    const longitudePadding = meters / (METERS_PER_DEGREE_LATITUDE * longitudeScale);

    return [
        minLng - longitudePadding,
        minLat - latitudePadding,
        maxLng + longitudePadding,
        maxLat + latitudePadding,
    ];
}

function mergeGeoJsonBounds(currentBounds, nextBounds) {
    if (!currentBounds) {
        return nextBounds;
    }

    return [
        Math.min(currentBounds[0], nextBounds[0]),
        Math.min(currentBounds[1], nextBounds[1]),
        Math.max(currentBounds[2], nextBounds[2]),
        Math.max(currentBounds[3], nextBounds[3]),
    ];
}

function getAggregateGeoJsonBounds(entries, paddingMeters = 0) {
    const values = Array.isArray(entries) ? entries : [entries];
    let aggregateBounds = null;

    values.forEach((entry) => {
        const bounds = getGeoJsonBounds(entry);
        if (!bounds) {
            return;
        }

        aggregateBounds = mergeGeoJsonBounds(aggregateBounds, bounds);
    });

    if (!aggregateBounds) {
        return null;
    }

    return padGeoJsonBoundsByMeters(aggregateBounds, paddingMeters);
}

function isMapLibreLikeMap(map) {
    return typeof map?.getStyle === 'function'
        && typeof map?.getSource === 'function'
        && typeof map?.project === 'function';
}

function createFitBoundsForMap(map, entries, leaflet, options = {}) {
    const paddingMeters = Number(options.paddingMeters ?? 0);
    const aggregateBounds = getAggregateGeoJsonBounds(entries, paddingMeters);

    if (!aggregateBounds) {
        return null;
    }

    if (isMapLibreLikeMap(map)) {
        const [minLng, minLat, maxLng, maxLat] = aggregateBounds;
        return [
            [minLng, minLat],
            [maxLng, maxLat],
        ];
    }

    return createLeafletBounds(entries, leaflet, { paddingMeters }) ?? [
        [aggregateBounds[1], aggregateBounds[0]],
        [aggregateBounds[3], aggregateBounds[2]],
    ];
}

export function createLeafletBounds(entries, leaflet = getLeaflet(), options = {}) {
    if (!leaflet?.latLngBounds) {
        return null;
    }

    const values = Array.isArray(entries) ? entries : [entries];
    const aggregateBounds = leaflet.latLngBounds();
    let hasBounds = false;
    let paddedGeoJsonBounds = null;
    const paddingMeters = Number(options.paddingMeters ?? 0);

    values.forEach((entry) => {
        const bounds = getGeoJsonBounds(entry);
        if (!bounds) {
            return;
        }

        if (paddingMeters > 0) {
            paddedGeoJsonBounds = mergeGeoJsonBounds(paddedGeoJsonBounds, bounds);
            return;
        }

        if (extendLeafletBounds(aggregateBounds, bounds, leaflet)) {
            hasBounds = true;
        }
    });

    if (paddedGeoJsonBounds) {
        const bounds = padGeoJsonBoundsByMeters(paddedGeoJsonBounds, paddingMeters);
        hasBounds = extendLeafletBounds(aggregateBounds, bounds, leaflet);
    }

    return hasBounds ? aggregateBounds : null;
}

export function fitMapToGeoJsonBounds(map, entries, options = {}) {
    if (!map || typeof map.fitBounds !== 'function') {
        return false;
    }

    const { leaflet = getLeaflet(), paddingMeters = 0, ...fitOptions } = options;
    const bounds = createFitBoundsForMap(map, entries, leaflet, { paddingMeters });

    if (!bounds || (typeof bounds.isValid === 'function' && !bounds.isValid())) {
        return false;
    }

    map.fitBounds(bounds, fitOptions);
    return true;
}
